// pages/profile-page.tsx

import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLayout } from "../components/page-layout";
import { getProfile } from "../services/profile.service";
import { ProfileResponse } from "../models/profile-response";
import { Link } from "react-router-dom";

export const ProfilePage: React.FC = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0();
  const [profileData, setProfileData] = useState<ProfileResponse | null>(null);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await getProfile(accessToken);

        if (response.error) {
          setError(response.error.message);
        } else {
          setProfileData(response.data);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [getAccessTokenSilently]);

  if (isLoading || loadingProfile) {
    return (
      <PageLayout>
        <div className="content-layout">
          <div className="content__body">
            <p className="text-center text-[#9aa5b8]">Loading...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="content-layout">
          <div className="content__body">
            <p className="text-center text-red-400">Error: {error}</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!profileData) {
    return null;
  }

  // Helper function to display values
  const displayValue = (value: any, unit?: string) => {
    if (
      value === null ||
      value === undefined ||
      value === "None" ||
      value === "null" ||
      value === "Unknown"
    ) {
      return "Unknown";
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "Unknown";
    }
    if (unit) {
      return `${value} ${unit}`;
    }
    return value;
  };
  // Convert decimal min/km (e.g., 6.54) to '6 min 32 sec per km'
  const formatPace = (value: any) => {
    const num = Number(value);
    if (isNaN(num)) return String(value);
    const mins = Math.floor(num);
    const secs = Math.round((num - mins) * 60);
    const pad = (s: number) => s.toString().padStart(2, '0');
    return `${mins} min ${pad(secs)} sec / km`;
  };

  const HRMAX = "HR_MAX";

  return (
    <PageLayout>
      <div className="content-layout">
        <div className="content__body p-4">
          <div className="max-w-3xl mx-auto">

            {/* Explanatory Note */}
            <div className="mb-8">
              <p className="text-l text-[#9aa5b8]">
                To change the profile information recorded here, just{" "}
                <Link to="/chat" className="underline">
                  ask your AI coach!
                </Link>
              </p>
            </div>

            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">
                Personal Information
              </h3>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-[#9aa5b8] font-medium">
                    Preferred Name:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.preferred_name)}
                  </div>
                  <div className="text-[#9aa5b8] font-medium">
                    Birthdate:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.birthdate)}
                  </div>
                </div>
              </div>
            </div>

            {/* Goal Race Information */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">
                Goal Race Information
              </h3>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-[#9aa5b8] font-medium">Race Name:</div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.goal_race_name)}
                  </div>
                  <div className="text-[#9aa5b8] font-medium">Race Date:</div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.goal_race_date)}
                  </div>
                  <div className="text-[#9aa5b8] font-medium">
                    Race Distance:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.goal_race_distance, "km")}
                  </div>
                </div>
              </div>
            </div>

            {/* Thresholds */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">
                Thresholds
              </h3>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-[#9aa5b8] font-medium">
                    Threshold Heart Rate:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.threshold_heart_rate, "bpm")}
                  </div>
                  <div className="text-[#9aa5b8] font-medium">
                    Threshold Pace:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {(() => {
                      const raw = profileData.threshold_pace;
                      const num = Number(raw);
                      if (!isNaN(num)) {
                        return formatPace(num);
                      }
                      return displayValue(raw, 'min/km');
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Heart Rate Zones */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">
                Heart Rate Zones
              </h3>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                {profileData.heart_rate_zones &&
                profileData.heart_rate_zones.length > 1 ? (
                  (() => {
                    const heartRateZones = profileData.heart_rate_zones as number[];
                    return (
                      <div className="grid grid-cols-2 gap-4">
                        {heartRateZones.map((upperBoundary, index) => {
                          // Skip the first element (index 0) which represents "Too Easy"
                          if (index === 0) return null;
                          
                          const zoneNumber = index; // Use actual index as zone number (Zone 1, 2, etc.)
                          let min, max;
                          
                          min = heartRateZones[index - 1];
                          max = upperBoundary;
                          
                          let tooltipContent = "";
                          
                          // Set tooltip content based on zone number
                          if (zoneNumber === 1) {
                            tooltipContent = "Easy/Recovery Pace. At this pace, you are primarily burning fat for energy, and can carry on a conversation easily. Most athletes spend the vast majority of their time training in this zone as it is the easiest to recover from. With training, you can exercise in this zone for many hours or even days at a time. Your heart rate will be less than 83% of your threshold heart rate.";
                          } else if (zoneNumber === 2) {
                            tooltipContent = "Endurance. This is a comfortably hard pace. It's slower than your maximum effort for an hour, but not easy. You are burning a mix of fat and carbohydrates for energy. Depending on training, you can keep this pace up for 1-3 hours, and as such it is going to be your primary zone in a Marathon. Your heart rate will be 83-94% of your threshold heart rate.";
                          } else if (zoneNumber === 3) {
                            tooltipContent = "Threshold. By definition, this is the maximum pace that you can run in an hour in a race setting. Your limiting factor will usually be your Lactate Threshold, which is the point at which your body can no longer clear lactate from your blood as fast as it is being produced. This is a hard pace, and you can only keep it up for 20-60 minutes.";
                          } else if (zoneNumber === 4) {
                            tooltipContent = "V02 Max. This is the pace that you can run at for 5-8 minutes in a race setting. It is a very hard pace, and you will be breathing very hard. Having a high V02 Max is an exceptional predictor of longevity and overall health, but must be trained with caution as it is very stressful on the body. Your heart rate will be around 106% of your threshold heart rate.";
                          } else if (zoneNumber === 5) {
                            tooltipContent = "Maximum Effort. This is the pace that you can only run at for a couple of minutes at most. It is an all-out effort, and you will be breathing as hard as you can. Your heart rate will be above 106% of your threshold heart rate. This typically corresponds to maximal 200m to 800m efforts (less than 2 minutes) and relies primarily on anaerobic glycolisis for energy.";
                          }
                          
                          return (
                            <React.Fragment key={`Zone-${zoneNumber}`}>
                              <div className="text-[#9aa5b8] font-medium group relative">
                                Zone {zoneNumber}:
                                <div className="absolute left-0 -top-2 transform -translate-y-full invisible group-hover:visible bg-[#242e42] text-[#e8ecf4] p-2 rounded shadow-lg text-sm max-w-xs z-10">
                                  {tooltipContent}
                                </div>
                              </div>
                              <div className="text-[#e8ecf4]">
                                {displayValue(min)} - {displayValue(max)} bpm
                              </div>
                            </React.Fragment>
                          );
                        })}
                        
                        {/* Add the final zone with tooltip */}
                        <div className="text-[#9aa5b8] font-medium group relative">
                          Zone {heartRateZones.length}: {/* Changed from heartRateZones.length - 1 to heartRateZones.length */}
                          <div className="absolute left-0 -top-2 transform -translate-y-full invisible group-hover:visible bg-[#242e42] text-[#e8ecf4] p-2 rounded shadow-lg text-sm max-w-xs z-10">
                            Maximum Effort. This is the pace that you can only run at for a couple of minutes at most. It is an all-out effort, and you will be breathing as hard as you can. Your heart rate will be above 106% of your threshold heart rate. This typically corresponds to maximal 200m to 800m efforts (less than 2 minutes) and relies primarily on anaerobic glycolisis for energy.
                          </div>
                        </div>
                        <div className="text-[#e8ecf4]">
                          {displayValue(heartRateZones[heartRateZones.length - 1])} - {displayValue(HRMAX)} bpm
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-[#e8ecf4]">Unknown</p>
                )}
              </div>
            </div>

            {/* Training Preferences */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">
                Training Preferences
              </h3>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-[#9aa5b8] font-medium">
                    Maximum Weekly Training Duration:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.maximum_weekly_training_duration, "hours")}
                  </div>
                  <div className="text-[#9aa5b8] font-medium">
                    Long Run Days:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.long_run_days)}
                  </div>
                  <div className="text-[#9aa5b8] font-medium">
                    Number of Workouts Per Week:
                  </div>
                  <div className="text-[#e8ecf4]">
                    {displayValue(profileData.number_of_workouts_per_week)}
                  </div>
                </div>
              </div>
            </div>

            {/* Developer */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">
                Developer
              </h3>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                <p className="text-[#9aa5b8] mb-4">
                  Build your own tools on top of your Strongifier data.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-[#9aa5b8] font-medium">API Keys:</div>
                  <div className="text-[#e8ecf4]">
                    <Link to="/api-keys" className="text-[#8fa3ff] hover:text-[#b3c1ff] underline">
                      Manage your API keys
                    </Link>
                  </div>
                  <div className="text-[#9aa5b8] font-medium">
                    Documentation:
                  </div>
                  <div className="text-[#e8ecf4]">
                    <Link to="/developer" className="text-[#8fa3ff] hover:text-[#b3c1ff] underline">
                      Developer API docs
                    </Link>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </PageLayout>
  );
};

